import { useSyncExternalStore } from 'react';

/**
 * User-pinned navigation destinations (Phase 2.8).
 *
 * A small external store (not per-hook state) so every consumer — the sidebar
 * Favorites section and the topbar pin control — stays in sync when one toggles a
 * favorite. Persisted per browser in localStorage; capped so Favorites never becomes
 * another unbounded list. Stored as registry item ids; the UI resolves and
 * permission-filters them at render, so a favorite the user can no longer access
 * simply does not appear.
 */
const KEY = 'connaro.favorites';
const MAX = 5;

function read(): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(value) ? (value as string[]) : [];
  } catch {
    return [];
  }
}

let favorites: string[] = read();
const listeners = new Set<() => void>();

function set(next: string[]): void {
  favorites = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage may be unavailable (private mode); favorites just won't persist.
  }
  for (const l of listeners) l();
}

export function toggleFavorite(id: string): void {
  set(
    favorites.includes(id) ? favorites.filter((x) => x !== id) : [id, ...favorites].slice(0, MAX),
  );
}

/** Reactive list of favorite ids, in most-recently-pinned order. */
export function useFavorites(): string[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => favorites,
  );
}
